/**
 * The root (web-plan.md F1): the router the desktop never needed — `/login`,
 * `/c/:channelId`, `/join/:channelId` (the https twin of
 * `echofloat://join/<id>`, finally a public link that works in a browser) —
 * and both theme axes (D57) applied the one way every rule in the shared
 * sheet fires: `data-theme` on <html> AND the `dark`/`window--contrast-increased`
 * classes on the `.window` element.
 */
import { Navigate, Route, Router, useNavigate, useParams } from "@solidjs/router";
import {
  type JSX,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";

import { currentLifetime, isCurrent } from "./lib/lifetime";
import { session } from "./lib/session";
import { swap } from "./lib/transition";
import { Runtime } from "./store/runtime";
import * as sounds from "./lib/sounds";
import { Login } from "./views/login";
import { Shell } from "./views/shell";

const rt = new Runtime();
// The host layer (F6): the ring is the one looping sound, exactly the
// desktop's split — the runtime says start/stop, the platform plays.
rt.onRing = (start) => (start ? sounds.ringStart() : sounds.ringStop());
// The dev handle the live acceptance drives (never in production builds).
if (import.meta.env.DEV) {
  (window as unknown as { __rt: Runtime }).__rt = rt;
}

function useTheme(): () => boolean {
  const media = window.matchMedia("(prefers-color-scheme: dark)");
  const contrastMedia = window.matchMedia("(prefers-contrast: more)");
  const [dark, setDark] = createSignal(media.matches);
  const [contrast, setContrast] = createSignal(contrastMedia.matches);
  const onDark = (e: MediaQueryListEvent) => setDark(e.matches);
  const onContrast = (e: MediaQueryListEvent) => setContrast(e.matches);
  media.addEventListener("change", onDark);
  contrastMedia.addEventListener("change", onContrast);
  onCleanup(() => {
    media.removeEventListener("change", onDark);
    contrastMedia.removeEventListener("change", onContrast);
  });
  createEffect(() => {
    document.documentElement.dataset["theme"] = dark() ? "dark" : "light";
    if (contrast()) document.documentElement.dataset["contrast"] = "increased";
    else delete document.documentElement.dataset["contrast"];
  });
  return createMemo(() => dark());
}

function WindowRoot(props: { children: JSX.Element }): JSX.Element {
  const dark = useTheme();
  const contrast = window.matchMedia("(prefers-contrast: more)");
  return (
    <div
      class={`window${dark() ? " window--dark" : ""}${contrast.matches ? " window--contrast-increased" : ""}`}
      tabindex="-1"
    >
      {props.children}
    </div>
  );
}

function LoginRoute(): JSX.Element {
  const navigate = useNavigate();
  createEffect(() => {
    if (rt.state.selfUserId !== 0 || (session() && rt.state.connected)) {
      swap("screen", () => navigate("/", { replace: true }));
    }
  });
  return (
    <WindowRoot>
      <Login rt={rt} t={rt.t} />
    </WindowRoot>
  );
}

function ShellRoute(): JSX.Element {
  const navigate = useNavigate();
  const params = useParams<{ channelId?: string }>();
  const dark = useTheme();

  onMount(() => {
    if (!session()) {
      swap("screen", () => navigate("/login", { replace: true }));
      return;
    }
    rt.resume();
  });

  // The URL is an input: /c/:id opens that channel once the rooms exist.
  createEffect(() => {
    const raw = params.channelId;
    if (raw === undefined) return;
    const id = Number(raw);
    if (!Number.isFinite(id)) return;
    if (rt.state.rooms.some((r) => r.channelId === id) && rt.state.selectedChannel !== id) {
      rt.openChannel(id);
    }
  });

  // ...and an output: the open channel writes itself back into the URL. Not
  // wrapped in a swap: both sides of this hop render the same ShellRoute, and
  // a view transition over identical content is a blur that says nothing.
  // When a channel click triggers it, it lands inside that click's own
  // "center" transition and is absorbed there.
  createEffect(() => {
    const open = rt.state.selectedChannel;
    if (open !== null && params.channelId !== String(open)) {
      navigate(`/c/${open}`, { replace: true });
    }
  });

  createEffect(() => {
    if (!session() && rt.state.selfUserId === 0 && !rt.state.busy) {
      // Logged out (401, auth_rejected, or by hand): back to the door.
      swap("screen", () => navigate("/login", { replace: true }));
    }
  });

  return (
    <WindowRoot>
      <Shell rt={rt} dark={dark()} />
    </WindowRoot>
  );
}

function JoinRoute(): JSX.Element {
  const params = useParams<{ channelId: string }>();
  const navigate = useNavigate();
  onMount(async () => {
    const lifetime = currentLifetime();
    if (!session()) {
      swap("screen", () => navigate("/login", { replace: true }));
      return;
    }
    rt.resume();
    const id = Number(params.channelId);
    const token = new URLSearchParams(location.search).get("token") ?? undefined;
    if (Number.isFinite(id)) {
      await rt.sync();
      if (!isCurrent(lifetime)) return;
      if (token) await rt.joinByToken(id, token);
      else await rt.join(id);
      if (!isCurrent(lifetime)) return;
      rt.openChannel(id);
    }
    swap("screen", () => navigate(`/c/${params.channelId}`, { replace: true }));
  });
  return (
    <WindowRoot>
      <div class="login">
        <div class="login__card echo-material-thick">
          <div class="login__title">EchoFloat</div>
        </div>
      </div>
    </WindowRoot>
  );
}

export function App(): JSX.Element {
  return (
    <Router>
      <Route path="/login" component={LoginRoute} />
      <Route path="/join/:channelId" component={JoinRoute} />
      <Route path="/c/:channelId" component={ShellRoute} />
      <Route path="/" component={ShellRoute} />
      <Route path="*" component={() => <Navigate href="/" />} />
    </Router>
  );
}
